import { env } from 'next-runtime-env';
import { useEffect } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';
import { createPkcePair, isOktaPkceEnabled, storePkceVerifierCookie } from '@/lib/pkce';

export default function SignInWithOkta() {
  const { query } = useNavigation();
  const callbackUrl = (query.callbackUrl as string) || env('NEXT_PUBLIC_OKTA_REDIRECT_URI_BASE') || '/';

  useEffect(() => {
    void (async () => {
      // Pure Okta redirect - no NextAuth
      console.log('SignInWithOkta: Redirecting directly to Okta');
      console.log('📍 Debug - Current URL:', window.location.href);
      console.log('📍 Debug - Search params callbackUrl:', callbackUrl);

      // Check for session timeout callback cookie (set during session timeout)
      const getSessionTimeoutCallback = () => {
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'session_timeout_callback') {
            try {
              // Decode base64 encoded path (used to avoid WAF triggers)
              return atob(value);
            } catch (e) {
              // Fallback for old format (URL encoded) if base64 decode fails
              console.warn('Failed to decode base64 session callback, trying URL decode:', e);
              return decodeURIComponent(value);
            }
          }
        }
        return null;
      };

      const sessionTimeoutCallback = getSessionTimeoutCallback();
      console.log('📍 Debug - Session timeout callback from cookie:', sessionTimeoutCallback);
      console.log('📍 Debug - Search params callbackUrl:', callbackUrl);

      const finalCallbackUrl = sessionTimeoutCallback || callbackUrl || '/';
      console.log('📍 Debug - Final callback URL that will be used:', finalCallbackUrl);

      // Store the callback URL in a cookie for the Okta callback to read
      document.cookie = `okta-callback-url=${encodeURIComponent(finalCallbackUrl)}; path=/; max-age=600; SameSite=Lax`;
      console.log('🍪 Debug - Stored callback URL in cookie for Okta callback:', finalCallbackUrl);

      if (sessionTimeoutCallback) {
        console.log('✅ Using session timeout callback URL:', sessionTimeoutCallback);
      } else {
        console.log('⚠️ No session timeout callback found, using default:', finalCallbackUrl);
      }

      // Check for required environment variables
      const oktaIssuer = env('NEXT_PUBLIC_OKTA_ISSUER');
      const oktaClientId = env('NEXT_PUBLIC_OKTA_CLIENT_ID');

      console.log('🔍 Debug - OKTA_ISSUER value:', oktaIssuer);
      console.log('🔍 Debug - OKTA_CLIENT_ID value:', oktaClientId);
      console.log('🔍 Debug - typeof OKTA_ISSUER:', typeof oktaIssuer);
      console.log('🔍 Debug - typeof OKTA_CLIENT_ID:', typeof oktaClientId);

      if (!oktaIssuer) {
        console.error('❌ NEXT_PUBLIC_OKTA_ISSUER is not defined or falsy:', oktaIssuer);
        return;
      }

      if (!oktaClientId) {
        console.error('❌ NEXT_PUBLIC_OKTA_CLIENT_ID is not defined or falsy:', oktaClientId);
        return;
      }

      // Construct Okta authorization URL
      const oktaAuthUrl = new URL(`${oktaIssuer}/oauth2/default/v1/authorize`);
      oktaAuthUrl.searchParams.set('client_id', oktaClientId);
      oktaAuthUrl.searchParams.set('response_type', 'code');
      oktaAuthUrl.searchParams.set('scope', 'offline_access openid profile email');

      // Debug current origin
      console.log('🌐 Debug - window.location.origin:', window.location.origin);
      console.log('🌐 Debug - window.location.href:', window.location.href);
      console.log('🌐 Debug - window.location.hostname:', window.location.hostname);
      console.log('🌐 Debug - window.location.protocol:', window.location.protocol);

      // Use NEXT_PUBLIC_REDIRECT_URI_BASE - fail if not configured
      const configuredRedirectBase = env('NEXT_PUBLIC_REDIRECT_URI_BASE');

      if (!configuredRedirectBase) {
        console.error('❌ NEXT_PUBLIC_REDIRECT_URI_BASE is required but not configured');
        throw new Error('Missing NEXT_PUBLIC_REDIRECT_URI_BASE configuration');
      }

      console.log('🎯 Debug - Using configured redirect URI base:', configuredRedirectBase);

      // Remove trailing slash and construct the full redirect URI
      const cleanBaseUrl = configuredRedirectBase.replace(/\/$/, '');
      const redirectUri = `${cleanBaseUrl}/api/auth/callback/okta`;
      console.log('🔗 Debug - Final redirect URI:', redirectUri);

      oktaAuthUrl.searchParams.set('redirect_uri', redirectUri);

      if (isOktaPkceEnabled()) {
        const { codeVerifier, codeChallenge } = await createPkcePair();
        oktaAuthUrl.searchParams.set('code_challenge', codeChallenge);
        oktaAuthUrl.searchParams.set('code_challenge_method', 'S256');
        storePkceVerifierCookie(codeVerifier);
        console.log('🔐 PKCE: code_challenge set, verifier stored in cookie');
      }

      // Use a simple random state for security instead of encoding callback URL
      const state = Math.random().toString(36).substring(2, 15);
      oktaAuthUrl.searchParams.set('state', state);

      // Store the state in a secure cookie for verification
      document.cookie = `okta-state=${state}; path=/; max-age=600; SameSite=Lax; Secure`;

      console.log('🔗 Debug - okta-state:', state);
      console.log('🔗 Redirecting to Okta URL:', oktaAuthUrl.toString());
      console.log('🎯 Will redirect to after auth (stored in cookie):', finalCallbackUrl);
      window.location.href = oktaAuthUrl.toString();
    })();
  }, [callbackUrl]);

  // Show error message if environment variables are missing
  const oktaIssuer = env('NEXT_PUBLIC_OKTA_ISSUER');
  const oktaClientId = env('NEXT_PUBLIC_OKTA_CLIENT_ID');

  if (!oktaIssuer || !oktaClientId) {
    console.error('Missing required environment variables');
    return;
  }

  return <div style={{ padding: '20px', textAlign: 'center' }}></div>;
}
